import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../../services";
import {
  LIVE_SUBSCRIPTION,
  connectedBranchCount,
  featureKeyForApp,
  formatDate,
  formatMinor,
  heldCoveringPlan,
  planForApp,
  resolvedCoveringBranchLimit,
} from "./subscriptionCatalog";
import riyalIcon from "./assets/riyal.svg";
import "./SubscriptionFlow.css";

function SubscriptionConnectBanner({ appId, variant = "section", onInfo, refreshKey = 0 }) {
  const { t, i18n } = useTranslation("translation");
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!appId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [bundleRes, connectionRes, appsRes] = await Promise.all([
          api.get("/subscriptions/bundle"),
          api.get("/subscriptions/bundle/connections").catch(() => ({ data: { connections: [] } })),
          api.get("/available-apps/categorized"),
        ]);
        if (cancelled) return;
        const catalog = [
          ...(appsRes.data?.categories?.addOns?.apps || []),
          ...(appsRes.data?.categories?.integrations?.apps || []),
        ];
        const app = catalog.find((entry) => String(entry._id || entry.id) === String(appId));
        const subscription = bundleRes.data?.subscription || null;
        const plans = bundleRes.data?.plans || [];
        const connections = connectionRes.data?.connections || [];
        const key = featureKeyForApp(app);
        const matched = connections.filter((row) => {
          if (app && row.appId) return String(row.appId) === String(appId);
          return Boolean(key && row.featureKey === key);
        });
        const global = heldCoveringPlan(subscription, plans);
        const item = (subscription?.items || []).find((row) => row.featureKey === key || row.planCode === planForApp(app, plans)?.code);
        const entitled = Number(subscription?.entitlements?.[key]);
        const limit = global
          ? resolvedCoveringBranchLimit(subscription, global)
          : (Number.isFinite(entitled) && entitled > 0 ? entitled : Number(item?.quantity || 0));
        const renewsAt = connectionRes.data?.renewsAt || subscription?.scheduledChange?.effectiveAt || subscription?.renewsAt;
        setInfo({
          app,
          subscription,
          global,
          limit,
          used: app ? connectedBranchCount(app, connections) : matched.length,
          renewsAt,
          marked: matched.filter((row) => row.disconnectAtRenewal),
          freeCount: matched.filter((row) => row.mode === "free").length,
          plans,
        });
      } catch (err) {
        if (!cancelled) setInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appId, refreshKey]);

  useEffect(() => {
    if (onInfo) onInfo(info);
  }, [info, onInfo]);

  if (!info) return null;
  const freeNotice = info.freeCount > 0 ? (
    <div className="sf-pending">
      <span>{t("appsManagementPage.freeMode")}</span>
      <button type="button" className="sf-btn brand" onClick={() => navigate("/subscription")}>{t("ordersPage.upgrade")}</button>
    </div>
  ) : null;
  if (!LIVE_SUBSCRIPTION.has(info.subscription?.status) || !info.limit) return freeNotice;
  const date = formatDate(info.renewsAt, i18n.language);
  const names = info.marked.map((row) => (i18n.language === "ar" && row.nameAr) || row.name).filter(Boolean);
  const full = info.used >= info.limit;
  const width = `${Math.min(100, Math.round((info.used / info.limit) * 100))}%`;

  if (variant === "meter") {
    const unit = info.app ? planForApp(info.app, info.plans || []) : null;
    return (
      <div className="aa-bmeter">
        <div className="aa-bm-top sf-usage-row">
          <span className="aa-bm-label">
            <b>{info.used}</b> {t("addAppPage.ofBranchesUsed", { total: info.limit })}
          </span>
          <span className="sf-meter aa-meter-sm" aria-hidden="true"><i style={{ width }} /></span>
        </div>
        <small>
          {t("addAppPage.sharedByStores")}
          {unit?.amount ? (
            <>
              {" · "}
              <span className="sf-money">
                <img src={riyalIcon} alt="" width={12} height={14} />
                {t("addAppPage.perBranchMo", { amount: formatMinor(unit.amount, i18n.language) })}
              </span>
            </>
          ) : null}
          {" · "}
          <button type="button" className="sf-btn link" onClick={() => navigate("/subscription")}>{t("addAppPage.changePlan")}</button>
        </small>
        {names.length ? <small>{t("appsManagementPage.disconnectsOn", { date, names: names.join(", ") })}</small> : null}
        {freeNotice}
        {full ? (
          <button
            type="button"
            className="sf-btn brand"
            onClick={() => navigate(info.global ? "/subscription" : `/subscription?addBranch=${appId}`)}
          >
            {info.global ? t("subscriptionFlow.chooseGlobal") : t("appsManagementPage.addBranch")}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <section className="sf-section" style={{ marginTop: 12 }}>
      <div className="sf-usage sf-usage-row">
        <span className="aa-bm-label">
          {t("appsManagementPage.meter", { used: info.used, total: info.limit })}
        </span>
        <span className="sf-meter aa-meter-sm" aria-hidden="true"><i style={{ width: `${Math.min(100, Math.round((info.used / info.limit) * 100))}%` }} /></span>
      </div>
      {names.length ? (
        <p className="sf-hint">{t("appsManagementPage.disconnectsOn", { date, names: names.join(", ") })}</p>
      ) : null}
      {full ? (
        <div className="sf-pending">
          <span>{t("appsManagementPage.allUsed", { count: info.limit })}</span>
          <button
            type="button"
            className="sf-btn brand"
            onClick={() => navigate(info.global ? "/subscription" : `/subscription?addBranch=${appId}`)}
          >
            {info.global ? t("subscriptionFlow.chooseGlobal") : t("appsManagementPage.addBranch")}
          </button>
        </div>
      ) : null}
      {freeNotice}
    </section>
  );
}

export default SubscriptionConnectBanner;
